import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

type LiveInviteEventType =
  | 'invite_link_opened'
  | 'invite_loaded'
  | 'login_screen_ready'
  | 'register_screen_ready'
  | 'auth_success'
  | 'joined_community'
  | 'invite_completed'
  | 'invite_error';

type LiveInviteMetadataValue = string | number | boolean | null;

interface LiveInviteTrackingContext {
  communityId: string;
  inviteGuid: string;
}

interface LiveInviteRecordOptions {
  route?: string;
  metadata?: Record<string, unknown>;
  status?: string;
}

const STORAGE_KEY = 'liveInviteTrackingContext';
const DEFAULT_TRACKING_API_URL = 'https://partnerapicms.dev.curaconcerto.org/';

@Injectable({
  providedIn: 'root'
})
export class LiveInviteTrackingService {
  private context: LiveInviteTrackingContext | null = this.readStoredContext();
  private readonly trackingApiUrl = this.normalizeBaseUrl(
    (environment as { liveInviteTrackingApiUrl?: string }).liveInviteTrackingApiUrl || DEFAULT_TRACKING_API_URL
  );
  private readonly source = 'invite_app';

  constructor(private http: HttpClient) {}

  setContext(input: { communityId?: string | null; inviteGuid?: string | null }): void {
    const communityId = String(input.communityId || '').trim();
    const inviteGuid = String(input.inviteGuid || '').trim();
    if (!communityId || !inviteGuid) {
      return;
    }

    this.context = { communityId, inviteGuid };
    this.storeContext(this.context);
  }

  record(eventType: LiveInviteEventType, options: LiveInviteRecordOptions = {}): void {
    if (!this.context) {
      this.context = this.readStoredContext();
    }
    if (!this.context) {
      return;
    }

    const payload = {
      communityId: this.context.communityId,
      inviteGuid: this.context.inviteGuid,
      eventType,
      source: this.source,
      route: this.sanitizeRoute(options.route || this.currentRoute()),
      occurredAt: new Date().toISOString(),
      metadata: this.sanitizeMetadata(options.metadata || {}),
      ...(options.status ? { status: options.status } : {})
    };

    this.http.post(`${this.trackingApiUrl}live-invite/events`, payload).subscribe({
      error: () => {
        // Tracking is best-effort. Never block or change the invite flow when the API is unavailable.
      }
    });
  }

  private readStoredContext(): LiveInviteTrackingContext | null {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as Partial<LiveInviteTrackingContext>;
      const communityId = String(parsed.communityId || '').trim();
      const inviteGuid = String(parsed.inviteGuid || '').trim();
      return communityId && inviteGuid ? { communityId, inviteGuid } : null;
    } catch {
      return null;
    }
  }

  private storeContext(context: LiveInviteTrackingContext): void {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
    } catch {
      // Ignore storage errors; in-memory context is enough for the current page load.
    }
  }

  private currentRoute(): string {
    try {
      return `${window.location.pathname}${window.location.hash || ''}`;
    } catch {
      return '';
    }
  }

  private sanitizeRoute(route: string): string {
    return String(route || '').trim().slice(0, 512);
  }

  private sanitizeMetadata(metadata: Record<string, unknown>): Record<string, LiveInviteMetadataValue> {
    const sanitized: Record<string, LiveInviteMetadataValue> = {};
    Object.entries(metadata).forEach(([key, value]) => {
      if (!key) {
        return;
      }
      if (typeof value === 'string') {
        sanitized[key] = value.slice(0, 256);
      } else if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
        sanitized[key] = value;
      }
    });
    return sanitized;
  }

  private normalizeBaseUrl(url: string): string {
    const trimmed = String(url || DEFAULT_TRACKING_API_URL).trim();
    return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
  }
}
